import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

/**
 * A dot-matrix LED ticker, drawn on a canvas.
 *
 * Adapted from Originkit's Pixel LED Display. The glyph table and the column
 * builder are kept as they were — a 5×7 bitmap font is a lookup table, and
 * rewriting one is how you get a typo in the letter K. What changed is
 * everything around the render loop:
 *
 *   - it stops when scrolled off screen, because a permanently-running rAF on a
 *     long marketing page is a background tax nobody agreed to;
 *   - its colours come from the theme rather than props, so the board changes
 *     with the palette like every other surface here;
 *   - reduced motion freezes the scroll instead of animating it.
 */

type Direction = "left" | "right";
type DotShape = "round" | "square";

const ROWS = 7;
const GLYPH_COLS = 5;

const QUANTITY_MID = 10;
const QUANTITY_MIN_ROWS = 3;
const QUANTITY_MAX_ROWS = 21;

/** Rows of dots for a Dot Quantity of 1–20. Even either side of the midpoint. */
function rowsForQuantity(quantity: number): number {
  const q = Math.min(20, Math.max(1, Math.round(quantity)));
  const rows =
    q <= QUANTITY_MID
      ? QUANTITY_MIN_ROWS + ((q - 1) * (ROWS - QUANTITY_MIN_ROWS)) / (QUANTITY_MID - 1)
      : ROWS + ((q - QUANTITY_MID) * (QUANTITY_MAX_ROWS - ROWS)) / QUANTITY_MID;
  return Math.max(1, Math.round(rows));
}

const WORD_GAP_EXTRA = 3;
const SEPARATOR_PAD_EXTRA = 5;

// A 5×7 bitmap font, one byte per column, bit 0 = top row. Runs from space (32)
// through underscore (95), which is why lookups uppercase first.
const FONT: number[][] = [
  [0x00, 0x00, 0x00, 0x00, 0x00], // space
  [0x00, 0x00, 0x5f, 0x00, 0x00], // !
  [0x00, 0x07, 0x00, 0x07, 0x00], // "
  [0x14, 0x7f, 0x14, 0x7f, 0x14], // #
  [0x24, 0x2a, 0x7f, 0x2a, 0x12], // $
  [0x23, 0x13, 0x08, 0x64, 0x62], // %
  [0x36, 0x49, 0x55, 0x22, 0x50], // &
  [0x00, 0x05, 0x03, 0x00, 0x00], // '
  [0x00, 0x1c, 0x22, 0x41, 0x00], // (
  [0x00, 0x41, 0x22, 0x1c, 0x00], // )
  [0x14, 0x08, 0x3e, 0x08, 0x14], // *
  [0x08, 0x08, 0x3e, 0x08, 0x08], // +
  [0x00, 0x50, 0x30, 0x00, 0x00], // ,
  [0x08, 0x08, 0x08, 0x08, 0x08], // -
  [0x00, 0x60, 0x60, 0x00, 0x00], // .
  [0x20, 0x10, 0x08, 0x04, 0x02], // /
  [0x3e, 0x51, 0x49, 0x45, 0x3e], // 0
  [0x00, 0x42, 0x7f, 0x40, 0x00], // 1
  [0x42, 0x61, 0x51, 0x49, 0x46], // 2
  [0x21, 0x41, 0x45, 0x4b, 0x31], // 3
  [0x18, 0x14, 0x12, 0x7f, 0x10], // 4
  [0x27, 0x45, 0x45, 0x45, 0x39], // 5
  [0x3c, 0x4a, 0x49, 0x49, 0x30], // 6
  [0x01, 0x71, 0x09, 0x05, 0x03], // 7
  [0x36, 0x49, 0x49, 0x49, 0x36], // 8
  [0x06, 0x49, 0x49, 0x29, 0x1e], // 9
  [0x00, 0x36, 0x36, 0x00, 0x00], // :
  [0x00, 0x56, 0x36, 0x00, 0x00], // ;
  [0x00, 0x08, 0x14, 0x22, 0x41], // <
  [0x14, 0x14, 0x14, 0x14, 0x14], // =
  [0x41, 0x22, 0x14, 0x08, 0x00], // >
  [0x02, 0x01, 0x51, 0x09, 0x06], // ?
  [0x32, 0x49, 0x79, 0x41, 0x3e], // @
  [0x7e, 0x11, 0x11, 0x11, 0x7e], // A
  [0x7f, 0x49, 0x49, 0x49, 0x36], // B
  [0x3e, 0x41, 0x41, 0x41, 0x22], // C
  [0x7f, 0x41, 0x41, 0x22, 0x1c], // D
  [0x7f, 0x49, 0x49, 0x49, 0x41], // E
  [0x7f, 0x09, 0x09, 0x01, 0x01], // F
  [0x3e, 0x41, 0x41, 0x51, 0x32], // G
  [0x7f, 0x08, 0x08, 0x08, 0x7f], // H
  [0x00, 0x41, 0x7f, 0x41, 0x00], // I
  [0x20, 0x40, 0x41, 0x3f, 0x01], // J
  [0x7f, 0x08, 0x14, 0x22, 0x41], // K
  [0x7f, 0x40, 0x40, 0x40, 0x40], // L
  [0x7f, 0x02, 0x04, 0x02, 0x7f], // M
  [0x7f, 0x04, 0x08, 0x10, 0x7f], // N
  [0x3e, 0x41, 0x41, 0x41, 0x3e], // O
  [0x7f, 0x09, 0x09, 0x09, 0x06], // P
  [0x3e, 0x41, 0x51, 0x21, 0x5e], // Q
  [0x7f, 0x09, 0x19, 0x29, 0x46], // R
  [0x46, 0x49, 0x49, 0x49, 0x31], // S
  [0x01, 0x01, 0x7f, 0x01, 0x01], // T
  [0x3f, 0x40, 0x40, 0x40, 0x3f], // U
  [0x1f, 0x20, 0x40, 0x20, 0x1f], // V
  [0x7f, 0x20, 0x18, 0x20, 0x7f], // W
  [0x63, 0x14, 0x08, 0x14, 0x63], // X
  [0x03, 0x04, 0x78, 0x04, 0x03], // Y
  [0x61, 0x51, 0x49, 0x45, 0x43], // Z
  [0x00, 0x00, 0x7f, 0x41, 0x41], // [
  [0x02, 0x04, 0x08, 0x10, 0x20], // backslash
  [0x41, 0x41, 0x7f, 0x00, 0x00], // ]
  [0x04, 0x02, 0x01, 0x02, 0x04], // ^
  [0x40, 0x40, 0x40, 0x40, 0x40], // _
];

const EXTRA: Record<string, number[]> = {
  "●": [0x1c, 0x3e, 0x3e, 0x3e, 0x1c],
  "•": [0x00, 0x1c, 0x1c, 0x1c, 0x00],
  "·": [0x00, 0x00, 0x18, 0x00, 0x00],
  "°": [0x00, 0x03, 0x03, 0x00, 0x00],
  "★": [0x24, 0x2a, 0x7f, 0x2a, 0x24],
  "→": [0x08, 0x08, 0x2a, 0x1c, 0x08],
  "←": [0x08, 0x1c, 0x2a, 0x08, 0x08],
};

// Typographic punctuation folded to the ASCII the font actually has, so a
// curly apostrophe pasted from a doc does not come out as a blank column.
const FOLD: Record<string, string> = {
  "–": "-",
  "—": "-",
  "‘": "'",
  "’": "'",
  "“": '"',
  "”": '"',
};

function glyphFor(char: string): number[] {
  const extra = EXTRA[char];
  if (extra) return extra;
  const folded = FOLD[char] ?? char;
  const code = folded.toUpperCase().charCodeAt(0);
  return FONT[code - 32] ?? FONT[0]!;
}

/**
 * Flatten items and separators into one endless strip of dot columns. Word gaps
 * and the pad around a separator are counted in columns rather than spelled
 * with blank glyphs, so they stay tight as spread grows.
 */
function buildColumns(items: string[], separator: string, spread: number): number[] {
  const parts = (items ?? []).map((item) => item.trim()).filter(Boolean);
  const joiner = (separator ?? "").trim();
  const columns: number[] = [];
  const blanks = (count: number) => {
    for (let i = 0; i < count; i++) columns.push(0);
  };

  const writeText = (text: string) => {
    const chars = [...text];
    chars.forEach((char, index) => {
      if (char === " ") {
        blanks(spread + WORD_GAP_EXTRA);
        return;
      }
      const glyph = glyphFor(char);
      for (let i = 0; i < GLYPH_COLS; i++) columns.push(glyph[i]!);
      const next = chars[index + 1];
      if (next !== undefined && next !== " ") blanks(spread);
    });
  };

  if (parts.length === 0) {
    blanks(GLYPH_COLS + spread);
    return columns;
  }

  for (const part of parts) {
    writeText(part);
    blanks(spread + SEPARATOR_PAD_EXTRA);
    if (joiner) {
      writeText(joiner);
      blanks(spread + SEPARATOR_PAD_EXTRA);
    }
  }
  return columns;
}

/**
 * Read a theme colour as something canvas can use.
 *
 * The palette lives in CSS custom properties, and `fillStyle` cannot take
 * `var(--brand)`. Resolving through a probe element rather than parsing the
 * property means `color-mix()` and `oklch()` both come back as plain rgb.
 */
function resolveColor(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const probe = document.createElement("span");
  probe.style.color = `var(${name})`;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();
  return value || fallback;
}

interface LedTickerProps {
  items?: string[];
  separator?: string;
  /** Dot columns per second. */
  speed?: number;
  direction?: Direction;
  /** Board height in CSS pixels. */
  textSize?: number;
  dotSize?: number;
  dotQuantity?: number;
  spread?: number;
  dotShape?: DotShape;
  glow?: boolean;
  style?: CSSProperties;
}

export function LedTicker({
  items = ["LED", "DISPLAY"],
  separator = "●",
  speed = 20,
  direction = "left",
  textSize = 120,
  dotSize = 18,
  dotQuantity = 10,
  spread = 1,
  dotShape = "round",
  glow = true,
  style,
}: LedTickerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Bumped whenever the palette changes, to re-resolve the two colours.
  const [themeTick, setThemeTick] = useState(0);

  const itemsKey = JSON.stringify(items ?? []);
  const columns = useMemo(
    () => buildColumns(JSON.parse(itemsKey), separator, spread),
    [itemsKey, separator, spread],
  );

  // The theme picker writes custom properties onto <html> and toggles `dark`.
  // Watching that attribute is how the board follows a palette change without
  // the picker having to know this component exists.
  //
  // Coalesced, and that matters: the picker repaints the page on every hover,
  // and a tick per mutation would tear down and rebuild the whole render loop
  // once per mouse move across a list of 39.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const mo = new MutationObserver(() => {
      clearTimeout(t);
      t = setTimeout(() => setThemeTick((n) => n + 1), 150);
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["style", "class"] });
    return () => {
      clearTimeout(t);
      mo.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    const canvas = el;

    const onColor = resolveColor("--brand", "#e6b450");
    // The unlit dots: the board's own grid, barely there. Mixed toward the
    // background rather than a fixed grey so it stays subtle on light themes.
    const offColor = resolveColor("--border", "rgba(255,255,255,0.08)");

    const total = columns.length;
    if (!total) return;

    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;

    let alive = true;
    let raf = 0;
    let last = 0;
    let offset = 0;
    // Off-screen boards do not animate. This page is long, and a canvas that
    // keeps painting below the fold costs battery for nothing.
    let onScreen = true;

    let dpr = 1;
    let cell = 1;
    let radius = 1;
    let visibleCols = 0;
    let boardRows = ROWS;
    let yPad = 0;
    let scale = 1;
    let rowSource: number[] = [];

    function measure() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth || 600;
      const h = canvas.clientHeight || 120;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      boardRows = rowsForQuantity(dotQuantity);
      scale = boardRows / ROWS;
      cell = Math.max(1, (textSize / boardRows) * dpr);
      radius = (dotSize * dpr) / 2;
      visibleCols = Math.ceil(canvas.width / cell) + 1;
      yPad = (canvas.height - boardRows * cell) / 2;
      rowSource = [];
      for (let row = 0; row < boardRows; row++) {
        rowSource.push(Math.min(ROWS - 1, Math.floor(row / scale)));
      }
    }

    function dotPath(x: number, y: number) {
      if (dotShape === "square") {
        const s = radius * 2;
        ctx!.rect(x - radius, y - radius, s, s);
      } else {
        ctx!.moveTo(x + radius, y);
        ctx!.arc(x, y, radius, 0, Math.PI * 2);
      }
    }

    function draw() {
      const c = ctx!;
      c.clearRect(0, 0, canvas.width, canvas.height);

      const boardOffset = offset * scale;
      const whole = Math.floor(boardOffset);
      const shift = (boardOffset - whole) * cell;
      const sign = direction === "right" ? -1 : 1;
      const xBase = -sign * shift;

      // Unlit dots first: the whole board, so the lit ones read as a message on
      // a panel rather than as floating specks.
      c.beginPath();
      for (let cx = 0; cx < visibleCols; cx++) {
        const px = xBase + cx * cell + cell / 2;
        for (let row = 0; row < boardRows; row++) {
          dotPath(px, yPad + row * cell + cell / 2);
        }
      }
      c.fillStyle = offColor;
      c.fill();

      // Lit dots.
      c.beginPath();
      for (let cx = 0; cx < visibleCols; cx++) {
        const source = Math.floor((cx + sign * whole) / scale);
        const index = ((source % total) + total) % total;
        const bits = columns[index];
        if (!bits) continue;
        const px = xBase + cx * cell + cell / 2;
        for (let row = 0; row < boardRows; row++) {
          if (!((bits >> rowSource[row]!) & 1)) continue;
          dotPath(px, yPad + row * cell + cell / 2);
        }
      }

      if (glow) {
        c.save();
        c.globalCompositeOperation = "lighter";
        c.shadowColor = onColor;
        c.shadowBlur = radius * 2;
        c.globalAlpha = 0.6;
        c.fillStyle = onColor;
        c.fill();
        c.restore();
      }
      c.fillStyle = onColor;
      c.fill();
    }

    function frame(time: number) {
      if (!alive) return;
      if (onScreen) {
        const dt = last ? Math.min((time - last) / 1000, 0.05) : 0;
        offset = (offset + speed * dt) % total;
        draw();
      }
      // `last` tracks real time even while paused, so scrolling back does not
      // make the board leap forward by however long it was out of view.
      last = time;
      raf = requestAnimationFrame(frame);
    }

    measure();
    draw();

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            measure();
            draw();
          })
        : null;
    ro?.observe(canvas);

    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(([e]) => {
            onScreen = e?.isIntersecting ?? true;
          })
        : null;
    io?.observe(canvas);

    if (!still) raf = requestAnimationFrame(frame);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      ro?.disconnect();
      io?.disconnect();
    };
  }, [
    columns,
    speed,
    direction,
    textSize,
    dotSize,
    dotQuantity,
    dotShape,
    glow,
    themeTick,
  ]);

  return <canvas ref={canvasRef} aria-hidden="true" style={{ ...style, display: "block", width: "100%", height: "100%" }} />;
}

export default LedTicker;
